import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Typography,
  drawerClasses,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { logout } from "../../../api/auth";
import { clearNotifications } from "../../../store/Notification";
import { resetSessionAuth } from "../../../store/Session";
import { StoreInterfaces } from "../../../types/store/StoreInterfaces";
import NotificationMenu from "../Menus/NotificationMenu";
import MenuContent from "./MenuContent";
import ViewSwitcher from "./ViewSwitcher";

interface SideMenuMobileProps {
  open: boolean;
  onClose: () => void;
}

export default function SideMenuMobile({
  open,
  onClose,
}: Readonly<SideMenuMobileProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const session = useSelector((state: StoreInterfaces) => state.session);
  const userName = session?.name ?? session?.email;

  const handleLogoutClick = () => {
    logout()
      .then(() => {
        dispatch(resetSessionAuth());
        dispatch(clearNotifications());
        navigate("/");
      })
      .catch((error) => {
        console.error(error);
        dispatch(resetSessionAuth());
        dispatch(clearNotifications());
        navigate("/");
      })
      .finally(() => onClose());
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        [`& .${drawerClasses.paper}`]: {
          backgroundImage: "none",
          backgroundColor: "background.paper",
        },
      }}
    >
      <Stack sx={{ maxWidth: "70dvw", height: "100%" }}>
        <Stack
          direction="row"
          sx={{ p: 2, pb: 0, gap: 1, alignItems: "center" }}
        >
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: "center",
              flexGrow: 1,
              p: 1,
              minWidth: 0,
            }}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {userName?.[0]?.toUpperCase()}
            </Avatar>
            <Stack sx={{ minWidth: 0 }}>
              <Typography component="p" variant="subtitle1" noWrap>
                {userName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {session?.email}
              </Typography>
            </Stack>
          </Stack>

          {session?.accessToken && <NotificationMenu />}
        </Stack>

        <Divider sx={{ mt: 1 }} />

        {!session.isAdministrator && (
          <>
            <Box
              sx={{
                display: "flex",
                mt: "calc(var(--template-frame-height, 0px) + 4px)",
                p: 1.5,
              }}
            >
              <ViewSwitcher />
            </Box>

            <Divider sx={{ mt: 1 }} />
          </>
        )}

        <Stack sx={{ flexGrow: 1, overflow: "auto" }}>
          <MenuContent onNavigate={onClose} />
        </Stack>

        <Divider />

        <Stack sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ManageAccountsRoundedIcon />}
            onClick={() => {
              onClose();
              navigate("/profile");
            }}
          >
            {t("admin.users.my-profile")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            startIcon={<LogoutRoundedIcon />}
            onClick={handleLogoutClick}
          >
            {t("generic.logout")}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
